'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import { UserAvatar } from '@/components/images/user-avatar';
import type { Referee } from '@/components/portfolio/referrals/types';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { defaultTimeZone } from '@/i18n/locales';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { useUserProfilePrefetchIntent } from '@/lib/sdk/prefetch';
interface RefereeRowCompactProps {
  referee: Referee;
}
export function RefereeRowCompact(t0) {
  const $ = _c(46);
  const {
    referee
  } = t0;
  const t = useTranslations("social.referrals");
  const tProfile = useTranslations("profile.next.summary.referrals");
  const locale = useLocale();
  const {
    formatD6
  } = useFormattedCurrency();
  const profileHref = `/portfolio/${referee.refereeUserId}`;
  const intent = useIntentRoutePrefetch(profileHref, useUserProfilePrefetchIntent(referee.refereeUserId).onMouseEnter);
  let t1;
  if ($[0] !== locale || $[1] !== referee.linkedAt) {
    t1 = new Date(referee.linkedAt).toLocaleDateString(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: defaultTimeZone
    });
    $[0] = locale;
    $[1] = referee.linkedAt;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const linkedDate = t1;
  let t2;
  if ($[3] !== referee.displayName || $[4] !== referee.handle || $[5] !== referee.refereeUserId) {
    t2 = referee.displayName ?? referee.handle ?? referee.refereeUserId.slice(0, 8);
    $[3] = referee.displayName;
    $[4] = referee.handle;
    $[5] = referee.refereeUserId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const displayName = t2;
  const hasDownstream = referee.hasDownstream === true;
  const t3 = referee.avatarPreferences?.rasterise ?? false;
  let t4;
  if ($[7] !== referee.avatarNsfw || $[8] !== referee.avatarUrl || $[9] !== referee.displayName || $[10] !== referee.refereeUserId || $[11] !== t3) {
    t4 = <UserAvatar userId={referee.refereeUserId} displayName={referee.displayName} avatarUrl={referee.avatarUrl} nsfw={referee.avatarNsfw} size="sm" rasterise={t3} />;
    $[7] = referee.avatarNsfw;
    $[8] = referee.avatarUrl;
    $[9] = referee.displayName;
    $[10] = referee.refereeUserId;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== displayName) {
    t5 = <span className="truncate text-body font-medium text-foreground-primary">{displayName}</span>;
    $[13] = displayName;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== linkedDate) {
    t6 = <span className="text-caption text-foreground-tertiary">{linkedDate}</span>;
    $[15] = linkedDate;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== hasDownstream || $[18] !== tProfile) {
    t7 = hasDownstream ? <span className="text-micro text-foreground-secondary">{tProfile("downstream_badge_tooltip")}</span> : null;
    $[17] = hasDownstream;
    $[18] = tProfile;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
    t8 = <div className="flex min-w-0 flex-col gap-0.5">{t5}{t6}{t7}</div>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== intent || $[25] !== profileHref || $[26] !== t4 || $[27] !== t8) {
    t9 = <Link href={profileHref} prefetch={false} className="flex min-w-0 items-center gap-3" {...intent}>{t4}{t8}</Link>;
    $[24] = intent;
    $[25] = profileHref;
    $[26] = t4;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== formatD6 || $[30] !== referee.earningsGenerated) {
    t10 = formatD6(referee.earningsGenerated);
    $[29] = formatD6;
    $[30] = referee.earningsGenerated;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10) {
    t11 = <span className="text-body font-medium text-foreground-primary">{t10}</span>;
    $[32] = t10;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== formatD6 || $[35] !== referee.totalVolume || $[36] !== t) {
    t12 = t("referee_volume_suffix", {
      vol: formatD6(referee.totalVolume)
    });
    $[34] = formatD6;
    $[35] = referee.totalVolume;
    $[36] = t;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t12) {
    t13 = <span className="text-caption text-foreground-tertiary">{t12}</span>;
    $[38] = t12;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t11 || $[41] !== t13) {
    t14 = <div className="flex shrink-0 flex-col items-end gap-0.5 tabular-nums">{t11}{t13}</div>;
    $[40] = t11;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t14 || $[44] !== t9) {
    t15 = <div className="flex items-center justify-between gap-3">{t9}{t14}</div>;
    $[43] = t14;
    $[44] = t9;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  return t15;
}